<template>
    <section class="sc-section">
        <div class="sc-section-title">
            <h2>
                {{ $t("제어 대상") }} <span v-if="warn" class="sc-warn-mark" :title="$t('확인이 필요합니다')">!</span>
            </h2>
            <span class="sc-meta">{{ $t("유형을 고르면 아래에 대상 선택 영역이 열립니다") }}</span>
        </div>

        <div class="sc-type-grid">
            <button
                v-for="type in typeList"
                :key="type.equipType"
                type="button"
                :class="{ 'is-on': isChecked(type.equipType) }"
                :aria-pressed="String(isChecked(type.equipType))"
                @click="toggleType(type)"
            >
                <span class="n">{{ type.equipTypeName }}</span>
                <span class="c">{{ typeCount(type.equipType) }}</span>
            </button>
        </div>
        <div v-if="typeList.length === 0" class="sc-meta">{{ $t("제어 가능한 설비 유형이 없습니다.") }}</div>

        <div v-if="checkedTypes.length" class="sc-target-panel">
            <div class="sc-tabs">
                <button
                    v-for="type in checkedTypes"
                    :key="type.equipType"
                    type="button"
                    class="sc-tab"
                    :class="{ 'is-active': activeType === type.equipType }"
                    @click="activeType = type.equipType"
                >
                    {{ type.equipTypeName }}
                    <span class="sc-pill">{{ selectedCount(type.equipType) }}</span>
                </button>
            </div>

            <div class="sc-target-tools">
                <div class="sc-segment" role="group">
                    <button type="button" :class="{ 'is-active': kind === 'all' }" @click="kind = 'all'">
                        {{ $t("전체") }} {{ activeGroups.length + activeEquips.length }}
                    </button>
                    <button type="button" :class="{ 'is-active': kind === 'group' }" @click="kind = 'group'">
                        {{ $t("그룹") }} {{ activeGroups.length }}
                    </button>
                    <button type="button" :class="{ 'is-active': kind === 'equip' }" @click="kind = 'equip'">
                        {{ $t("개별 설비") }} {{ activeEquips.length }}
                    </button>
                </div>
                <div style="display: flex; gap: 8px">
                    <button type="button" class="sc-btn" @click="$refs.GroupModal.show()">
                        + {{ $t("그룹 추가") }}
                    </button>
                    <button type="button" class="sc-btn" @click="$refs.EquipModal.show()">
                        + {{ $t("설비 추가") }}
                    </button>
                </div>
            </div>

            <div v-if="!visibleGroups.length && !visibleEquips.length" class="sc-meta" style="margin-top: 12px">
                {{ $t("선택된 그룹/설비가 없습니다. 위 버튼으로 추가하세요.") }}
            </div>
            <div v-else class="sc-target-grid is-cols-3">
                <div v-for="group in visibleGroups" :key="`g${group.sysNodeIdx}`" class="sc-target-chip">
                    <span class="sc-kind-badge is-group">{{ $t("그룹") }}</span>
                    <span class="n" :title="group.sysNodeName">{{ group.sysNodeName || `#${group.sysNodeIdx}` }}</span>
                    <button type="button" class="sc-target-remove" :aria-label="$t('삭제')" @click="removeGroup(group)">
                        ✕
                    </button>
                </div>
                <div v-for="equip in visibleEquips" :key="`e${equip.equipIdx}`" class="sc-target-chip">
                    <span class="sc-kind-badge">{{ $t("설비") }}</span>
                    <span class="n" :title="equip.equipName">{{ equip.equipName || `#${equip.equipIdx}` }}</span>
                    <button type="button" class="sc-target-remove" :aria-label="$t('삭제')" @click="removeEquip(equip)">
                        ✕
                    </button>
                </div>
            </div>
        </div>

        <b-modal modal-class="sch-modal" ref="GroupModal" centered>
            <template #modal-header>
                <h5>{{ $t("설비 그룹 추가") }}</h5>
            </template>
            <template #modal-footer>
                <ModalFooter :confirm-label="$t('추가')" @confirm="addGroups" @cancel="$refs.GroupModal.hide()" />
            </template>
            <GroupPanel ref="GroupPanel" :type="activeType" />
        </b-modal>
        <b-modal modal-class="sch-modal" ref="EquipModal" centered>
            <template #modal-header>
                <h5>{{ $t("설비 추가") }}</h5>
            </template>
            <template #modal-footer>
                <ModalFooter :confirm-label="$t('추가')" @confirm="addEquips" @cancel="$refs.EquipModal.hide()" />
            </template>
            <EquipPanel ref="EquipPanel" :type="activeType" />
        </b-modal>
    </section>
</template>

<script>
import ModalFooter from "../ModalFooter.vue";
import GroupPanel from "../../panels/GroupPanel.vue";
import EquipPanel from "../../panels/EquipPanel.vue";
import { fetchEquipsOfType } from "../../utils/scheduleApi";
import * as popupMessages from "@src/consts/popupMessageConsts";

export default {
    components: { ModalFooter, GroupPanel, EquipPanel },
    props: {
        detail: { type: Object, required: true },
        warn: { type: Boolean, default: false },
    },
    data() {
        return {
            activeType: null,
            kind: "all",
            totals: {},
        };
    },
    computed: {
        /** 제어 모드가 있는 설비 유형만 스케줄 대상이 될 수 있다 */
        typeList() {
            return (this.$store.state.equipTypeList || [])
                .filter((item) => !this.isEmpty(item.ctrlModeCnt))
                .map((item) => ({ equipType: item.equipType, equipTypeName: item.equipTypeName }));
        },
        checkedTypes() {
            return this.typeList.filter((type) => this.isChecked(type.equipType));
        },
        activeGroups() {
            return this.detail.groups.filter((g) => g.equipType == this.activeType);
        },
        activeEquips() {
            return this.detail.equips.filter((e) => e.equipType == this.activeType);
        },
        visibleGroups() {
            return this.kind === "equip" ? [] : this.activeGroups;
        },
        visibleEquips() {
            return this.kind === "group" ? [] : this.activeEquips;
        },
    },
    watch: {
        checkedTypes: {
            immediate: true,
            handler(types) {
                if (!types.some((t) => t.equipType === this.activeType)) {
                    this.activeType = types.length ? types[0].equipType : null;
                }
            },
        },
    },
    created() {
        // 설비 유형 카드에 표시할 전체 설비 수
        this.typeList.forEach(async (type) => {
            const list = await fetchEquipsOfType(type.equipType);
            this.$set(this.totals, type.equipType, list.length);
        });
    },
    methods: {
        isChecked(equipType) {
            return this.detail.targets.some((t) => t.equipType == equipType);
        },
        selectedCount(equipType) {
            return (
                this.detail.groups.filter((g) => g.equipType == equipType).length +
                this.detail.equips.filter((e) => e.equipType == equipType).length
            );
        },
        typeCount(equipType) {
            if (this.isChecked(equipType)) return `${this.$t("선택")} ${this.selectedCount(equipType)}`;
            return this.totals[equipType] !== undefined ? `${this.totals[equipType]}${this.$t("대")}` : "";
        },
        async toggleType(type) {
            const { equipType } = type;
            if (!this.isChecked(equipType)) {
                this.detail.targets.push({ equipType });
                this.activeType = equipType;
                return;
            }
            if (this.selectedCount(equipType) > 0) {
                const confirm = await this.alertConfirmWarning(
                    `${type.equipTypeName} ${this.$t("유형을 해제하면 선택한 그룹/설비가 함께 제거됩니다. 계속하시겠습니까?")}`,
                );
                if (!confirm.value) return;
            }
            this.detail.targets = this.detail.targets.filter((t) => t.equipType != equipType);
            this.detail.groups = this.detail.groups.filter((g) => g.equipType != equipType);
            this.detail.equips = this.detail.equips.filter((e) => e.equipType != equipType);
        },
        removeGroup(group) {
            this.detail.groups = this.detail.groups.filter((g) => g.sysNodeIdx !== group.sysNodeIdx);
        },
        removeEquip(equip) {
            this.detail.equips = this.detail.equips.filter((e) => e.equipIdx !== equip.equipIdx);
        },
        addGroups() {
            const picked = this.$refs.GroupPanel.groupCheckList();
            if (this.isEmpty(picked)) {
                this.alertNoti(popupMessages.SCHEDULE_CONTROL_SELECT_GROUP_ADD_NOTI_MESSAGE);
                return;
            }
            const exists = new Set(this.detail.groups.map((g) => g.sysNodeIdx));
            picked
                .filter((g) => !exists.has(g.sysNodeIdx))
                .forEach((g) =>
                    this.detail.groups.push({
                        sysNodeIdx: g.sysNodeIdx,
                        equipType: g.equipType,
                        sysNodeName: g.sysNodeName,
                        enabled: "Y",
                    }),
                );
            this.$refs.GroupModal.hide();
        },
        addEquips() {
            const picked = this.$refs.EquipPanel.equipCheckList();
            if (this.isEmpty(picked)) {
                this.alertNoti(popupMessages.SCHEDULE_CONTROL_SELECT_EQUIP_ADD_NOTI_MESSAGE);
                return;
            }
            const exists = new Set(this.detail.equips.map((e) => e.equipIdx));
            picked
                .filter((e) => !exists.has(e.equipIdx))
                .forEach((e) =>
                    this.detail.equips.push({
                        equipIdx: e.equipIdx,
                        equipType: e.equipType,
                        equipName: e.equipName,
                        enabled: "Y",
                    }),
                );
            this.$refs.EquipModal.hide();
        },
    },
};
</script>
